import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, Pill } from "@atlas/ui/web";
import type { PedidoDoServidor } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import {
  MatriculaInfo,
  readActiveMatricula,
  hydrateMatriculas,
  STORAGE_KEY_ID,
  STORAGE_KEY_META,
} from "../../lib/matricula-data";
import { OutrosDescontos, ROTULO_ASSUNTO, quemPresta } from "../../components/servidor/OutrosDescontos";

// MEUS BENEFICIOS (#276, epic #312): os planos, seguros e mensalidades que a
// consignataria opera no portal e que descontam no contracheque do servidor.
//
// Em cima, cada beneficio com as acoes que a API aceita (pedir alteracao, pedir
// cancelamento, autorizar). Embaixo, os pedidos: abertos com o prazo de
// resposta, respondidos com o que a consignataria disse. E' aqui que o servidor
// fica sabendo do "recusado" e do motivo — o e-mail e' so' o aviso.
//
// Nao confundir com "Vantagens" (/servidor/beneficios): a vitrine de parceiros,
// que nao desconta em folha.

const fmtData = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");

function situacaoDoPedido(p: PedidoDoServidor): { texto: string; variant: "pendente" | "aceita" | "rejeitada" | "expirado" } {
  if (p.status === "atendida") return { texto: "atendido", variant: "aceita" };
  if (p.status === "recusada") return { texto: "recusado", variant: "rejeitada" };
  return p.vencida ? { texto: "prazo vencido", variant: "expirado" } : { texto: "aguardando resposta", variant: "pendente" };
}

export function ServidorMeusBeneficios() {
  const [info, setInfo] = useState<MatriculaInfo | null>(() => readActiveMatricula());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_META || e.key === STORAGE_KEY_ID) setInfo(readActiveMatricula());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const pedidos = useQuery({
    queryKey: ["servidor", "pedidos-beneficios"],
    queryFn: () => atlas.servidor.minhasSolicitacoesConsignacao(),
    // A resposta da consignataria chega a qualquer momento; a tela aberta acompanha.
    refetchInterval: 30_000,
    refetchOnMount: "always",
  });

  // A lista de beneficios mora em /me/matriculas: recarrega ao entrar.
  useEffect(() => {
    void hydrateMatriculas().then(() => setInfo(readActiveMatricula()));
  }, []);

  const recarregar = async () => {
    await hydrateMatriculas();
    setInfo(readActiveMatricula());
    await pedidos.refetch();
  };

  const consignacoes = info?.consignacoesFacultativas;

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <header>
        <h1 style={{ margin: 0, fontSize: "1.4rem" }}>Meus benefícios</h1>
        <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--text-muted)" }}>
          Plano de saúde, odontológico, seguro e mensalidades descontados no seu contracheque. Peça alteração ou
          cancelamento aqui — quem responde é a consignatária, e você acompanha a resposta nesta tela.
        </p>
      </header>

      {consignacoes !== undefined && consignacoes !== null && consignacoes.length === 0 ? (
        <Card>
          <p style={{ margin: 0, fontSize: 14, color: "var(--text-muted)" }}>
            Nenhum benefício descontado na sua folha nesta matrícula.
          </p>
        </Card>
      ) : (
        <OutrosDescontos
          consignacoes={consignacoes}
          onMudou={recarregar}
          titulo="Benefícios descontados em folha"
          subtitulo="O valor mensal e quem presta o serviço. Os botões aparecem quando o pedido é possível."
        />
      )}

      <section style={{ marginTop: 32 }} aria-labelledby="meus-pedidos-titulo">
        <h2 id="meus-pedidos-titulo" style={{ fontSize: "1.05rem", margin: "0 0 4px" }}>
          Meus pedidos
        </h2>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--text-muted)" }}>
          O que você pediu e o que a consignatária respondeu.
        </p>
        {pedidos.isLoading ? (
          <Card>
            <p style={{ margin: 0, fontSize: 13 }}>Carregando…</p>
          </Card>
        ) : pedidos.isError ? (
          <Card>
            <p role="alert" style={{ margin: 0, fontSize: 13 }}>
              {mensagemDeErro(pedidos.error, "Não foi possível carregar os seus pedidos.")}
            </p>
          </Card>
        ) : (pedidos.data?.itens ?? []).length === 0 ? (
          <Card>
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>Você ainda não fez nenhum pedido.</p>
          </Card>
        ) : (
          <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 12 }}>
            {pedidos.data!.itens.map((p) => {
              const s = situacaoDoPedido(p);
              const prestador = quemPresta(p.beneficio);
              const oQue = p.tipo === "alteracao" ? "Alteração" : "Cancelamento";
              return (
                <li key={p.id}>
                  <Card>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 700 }}>
                          {oQue}
                          {p.assunto ? ` — ${ROTULO_ASSUNTO[p.assunto]}` : ""}
                        </div>
                        <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>
                          {p.beneficio.rotulo}
                          {prestador ? ` · ${prestador}` : ""}
                        </div>
                      </div>
                      <Pill variant={s.variant}>{s.texto}</Pill>
                    </div>
                    {p.motivo ? (
                      <p style={{ margin: "10px 0 0", fontSize: 13 }}>
                        <span style={{ color: "var(--text-muted)" }}>Você escreveu: </span>
                        {p.motivo}
                      </p>
                    ) : null}
                    <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
                      Enviado em {fmtData(p.solicitadaEm)}
                      {p.status === "aberta"
                        ? ` · resposta até ${fmtData(p.responderAte)}`
                        : p.respondidaEm
                          ? ` · respondido em ${fmtData(p.respondidaEm)}`
                          : ""}
                    </p>
                    {p.resposta ? (
                      <div
                        style={{
                          marginTop: 10,
                          padding: "10px 12px",
                          borderRadius: 8,
                          background: "var(--surface-2, var(--bg))",
                          border: "1px solid var(--border)",
                          fontSize: 13,
                        }}
                      >
                        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)" }}>
                          {p.status === "recusada" ? "Motivo da recusa" : "Resposta"}
                          {p.respondidaPor === "averbadora" ? " (pela averbadora)" : ""}
                        </div>
                        <div style={{ marginTop: 4 }}>{p.resposta}</div>
                      </div>
                    ) : null}
                  </Card>
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}
